/**
 * Current bill — C1.
 * Line-item breakdown with PETCI tariff structure, PDF download, AI explainer CTA.
 * Atlas: C1.
 */
"use client";

import Link from "next/link";
import { ArrowLeft, HelpCircle, ChevronDown, ChevronUp, History } from "lucide-react";
import { useState } from "react";
import { meterId, type Meter, type BillRow } from "@petci/fixtures";
import { useActivePersona } from "@/lib/persona";
import { useBillState } from "@petci/shared-ui";
import { formatCurrency, formatDate, formatKwh, formatMonth, billDueIso } from "@/lib/format";
import { useT, useLocaleTag } from "@/lib/locale";
import { isHotelPersona, isPropertyManager, calculateBillDeltaPct, calculateMeterKwh, calculateMeterCost, calculatePortfolioTotal, calculateEnergyCharge, calculateDemandCharge, calculateMeterSharePct } from "@petci/shared-ui";
import { UsageSparkline } from "@/components/UsageSparkline";
import { TermTooltip } from "@/components/TermTooltip";
import ComingSoonPill from "@/components/ui/ComingSoonPill";
import { BillPdfButton } from "@/components/BillPdfButton";

export default function BillPage() {
  const persona = useActivePersona();
  const bill = persona.currentBill;
  const t = useT();
  const tag = useLocaleTag();
  const billState = useBillState(persona.id);

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <Link
            href="/home"
            className="p-1.5 rounded-lg text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 transition-colors duration-fast"
            aria-label={t("common.backToHome")}
          >
            <ArrowLeft size={18} aria-hidden />
          </Link>
          <div>
            <h1 className="text-2xl font-display font-bold tracking-tight text-neutral-900">
              {t("bill.currentBill")}
            </h1>
            <p className="text-sm text-neutral-500 mt-1">
              {t("bill.accountAddress", {
                account: persona.accountNumber,
                address: persona.serviceAddress.line1,
              })}
            </p>
          </div>
        </div>
        <span className="badge bg-brand-primary-hover text-white">
          {billState.isSettled
            ? `${t("billing.paidOn", { date: formatDate(billState.settledOnIso, tag) })} ✓`
            : t("bill.dueBadge", { date: formatDate(billDueIso(persona.currentBill.month), tag) })}
        </span>
      </div>

      {/* P3 — multi-meter aggregate */}
      {isHotelPersona(persona.id) ? (
        <HotelBillView persona={persona} bill={bill} />
      ) : isPropertyManager(persona.id) ? (
        <PortfolioBillView persona={persona} />
      ) : (
        <ResidentialBillView persona={persona} bill={bill} />
      )}

      {/* 12-month trend strip with month-on-month delta table */}
      <div className="card-surface space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-neutral-700">
            {t("bill.trendTitle")}
          </h2>
          <span className="text-xs text-neutral-500">{t("bill.trendPast12")}</span>
        </div>
        <UsageSparkline bills={persona.bills} />
        <div className="overflow-x-auto -mx-6 px-6">
          <table className="w-full text-xs text-left">
            <thead>
              <tr className="border-b border-neutral-200 text-neutral-500">
                <th className="pb-2 font-medium pr-4">{t("bill.thMonth")}</th>
                <th className="pb-2 font-medium text-right pr-4">{t("bill.thUsage")}</th>
                <th className="pb-2 font-medium text-right pr-4">{t("bill.thTotal")}</th>
                <th className="pb-2 font-medium text-right">{t("bill.thVsPrior")}</th>
              </tr>
            </thead>
            <tbody>
              {persona.bills.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-neutral-500 text-sm">
                    <History size={24} className="mx-auto mb-2" aria-hidden />
                    {t("bill.noPriorBills")}
                  </td>
                </tr>
              )}
              {persona.bills.slice(0, 6).map((row, i) => {
                const prior = persona.bills[i + 1];
                const delta = prior ? calculateBillDeltaPct(row.total, prior.total) : null;
                return (
                  <tr key={row.month} className="border-b border-neutral-50 hover:bg-neutral-50 transition-colors duration-fast">
                    <td className="py-1.5 pr-4 text-neutral-700 font-medium">{formatMonth(row.month, tag)}</td>
                    <td className="py-1.5 pr-4 text-right text-neutral-700 font-mono tabular-nums">
                      {row.usageKWh.toLocaleString()}
                    </td>
                    <td className="py-1.5 pr-4 text-right font-medium text-neutral-900 font-mono tabular-nums">
                      {formatCurrency(row.total)}
                    </td>
                    <td className="py-1.5 text-right">
                      {delta !== null ? (
                        <span className={`font-mono tabular-nums ${delta > 0 ? "text-semantic-warning-text" : "text-semantic-success-text"}`}>
                          {delta > 0 ? "+" : ""}
                          {delta.toFixed(1)}%
                        </span>
                      ) : (
                        <span className="text-neutral-500">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-neutral-500">
          {t("bill.trendFootnote")}
        </p>
      </div>
    </div>
  );
}

/**
 * Payment History entry point — rendered by EVERY bill branch view.
 *
 * F-1: this markup used to live inline in ResidentialBillView only, so P3
 * (hotel) and P4 (property manager) had no route to the page at all — the
 * screen titled Payment History was reachable by URL alone for two of the five
 * personas, which also made F-C2's session-row fix invisible to them. Extracted
 * rather than hoisted to the page shell so P1/P2 keep their established running
 * order (actions → history → AI explainer); the guard against a future branch
 * missing it again is the contract pin, which asserts every `*BillView` in this
 * file renders it.
 */
function PaymentHistoryLink() {
  const t = useT();
  return (
    <Link
      href="/bill/history"
      className="flex items-center gap-3 card-surface hover:shadow-card-hover transition-shadow duration-base group"
    >
      <span className="w-10 h-10 rounded-full bg-brand-secondary/10 flex items-center justify-center flex-shrink-0">
        <History size={18} className="text-brand-secondary" aria-hidden />
      </span>
      <div className="flex-1">
        <p className="text-sm font-semibold text-neutral-900">{t("bill.paymentHistory")}</p>
        <p className="text-xs text-neutral-500">{t("bill.paymentHistorySub")}</p>
      </div>
      <span className="text-xs font-medium text-brand-primary-text group-hover:translate-x-0.5 transition-transform duration-fast">→</span>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Residential / commercial bill view (P1, P2)
// ---------------------------------------------------------------------------
function ResidentialBillView({
  persona,
  bill,
}: {
  persona: ReturnType<typeof useActivePersona>;
  bill: BillRow;
}) {
  const t = useT();
  const billState = useBillState(persona.id);
  const electricCharge = calculateEnergyCharge(bill.electricRate, bill.usageKWh);
  const fuelCharge = calculateEnergyCharge(bill.fuelFactor, bill.usageKWh);

  return (
    <div className="space-y-4">
      {/* Line-item card */}
      <div className="card-surface space-y-0 overflow-hidden">
        <BillLine
          label={t("bill.electricCharge")}
          detail={t("bill.chargeDetail", { kwh: formatKwh(bill.usageKWh), rate: bill.electricRate.toFixed(4) })}
          amount={electricCharge}
        />
        <BillLine
          label={<TermTooltip term="Fuel Factor">{t("bill.fuelFactor")}</TermTooltip>}
          detail={t("bill.chargeDetail", { kwh: formatKwh(bill.usageKWh), rate: bill.fuelFactor.toFixed(4) })}
          amount={fuelCharge}
        />
        <BillLine
          label={persona.customerType === "COMMERCIAL_SMALL" ? t("bill.demandFee") : t("bill.customerCharge")}
          detail={persona.customerType === "COMMERCIAL_SMALL" ? t("bill.smallCommercial") : t("bill.monthlyBaseFee")}
          amount={bill.fees}
        />

        <div className="border-t border-neutral-200 px-6 py-3 flex items-center justify-between bg-neutral-50">
          <span className="text-sm text-neutral-500">{t("bill.subtotal")}</span>
          <span className="text-sm font-semibold text-neutral-900 font-mono tabular-nums">
            {formatCurrency(bill.subtotal)}
          </span>
        </div>

        <div className="border-t-2 border-brand-primary/20 px-6 py-4 flex items-center justify-between bg-white">
          <span className="text-base font-bold text-neutral-900">
            {t("bill.totalDue")}
          </span>
          <span className="text-2xl font-display font-bold text-brand-primary-text tabular-nums">
            {formatCurrency(bill.total)}
          </span>
        </div>
      </div>

      {/* Actions — F-2: the Pay CTA goes once the bill is settled. The header
          badge already reads "Paid on … ✓", so a live Pay button beside it is a
          contradiction, and D-2's cap makes it a dead end rather than a double
          charge. Statement figures above are untouched. */}
      <div className="flex flex-col sm:flex-row gap-3">
        {!billState.isSettled && (
          <Link href="/pay/amount" className="btn-primary flex-1 justify-center">
            {t("bill.payNow", { amount: formatCurrency(bill.total) })}
          </Link>
        )}
        <BillPdfButton personaId={persona.id} />
        <ComingSoonPill label={t("bill.shareBill")} className="flex-1 justify-center" />
      </div>

      <PaymentHistoryLink />

      {/* AI explainer CTA — all personas */}
      <Link
        href="/bill/explain"
        className="flex items-center gap-3 card-surface hover:shadow-lg transition-shadow duration-base group"
      >
        <span className="w-10 h-10 rounded-full bg-brand-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-brand-primary group-hover:text-white transition-colors duration-fast">
          <HelpCircle size={18} className="text-brand-primary-text group-hover:text-white" aria-hidden />
        </span>
        <div>
          <p className="text-sm font-semibold text-neutral-900">
            {t("bill.whyHigher")}
          </p>
          <p className="text-xs text-neutral-500">
            {t("bill.aiExplainerSub")}
          </p>
        </div>
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hotel multi-meter aggregate view (P3)
// ---------------------------------------------------------------------------
function HotelBillView({
  persona,
  bill,
}: {
  persona: ReturnType<typeof useActivePersona>;
  bill: BillRow;
}) {
  const t = useT();
  const billState = useBillState(persona.id);
  const meters = persona.meters;

  const aggregateKwh = bill.aggregateKwh ?? bill.usageKWh;
  const solarOffset = bill.solarOffsetKwh ?? 0;

  return (
    <div className="space-y-4">
      <div className="card-surface space-y-0 overflow-hidden">
        {/* Aggregate header */}
        <div className="px-6 py-4 bg-brand-primary/5 border-b border-neutral-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-neutral-700 font-medium uppercase tracking-wider">
                {t("bill.meterAggregate", { count: meters.length })}
              </p>
              <p className="text-sm mt-1">
                <span className="font-bold">{formatKwh(aggregateKwh)}</span>
                {solarOffset > 0 && (
                  <span className="text-semantic-success-text ml-2">
                    − {formatKwh(solarOffset)} {t("bill.solar")}
                  </span>
                )}
                <span className="text-neutral-700 ml-1">
                  = {formatKwh(bill.usageKWh)} {t("bill.net")}
                </span>
              </p>
            </div>
            <span className="text-2xl font-display font-bold text-brand-primary-text tabular-nums">
              {formatCurrency(bill.total)}
            </span>
          </div>
        </div>

        {/* Per-meter rows derived from fixture (DRY with hotel/portfolio and batch-pay) */}
        {meters.map((m, i) => {
          const pct = calculateMeterSharePct(m.costSharePct, meters.length);
          const shortName = m.description.replace(/\s*[(][^)]*[)]/g, "").trim();
          const meterKwh = calculateMeterKwh(pct, aggregateKwh);
          const meterTotal = calculateMeterCost(pct, bill.total);
          return (
            <MeterRow
              key={m.id}
              meterId={m.id}
              name={shortName}
              kwh={meterKwh}
              pct={pct}
              total={meterTotal}
              {...(m.id === meterId("M-40221-B") ? { anomaly: t("bill.kitchenAnomaly") } : {})}
              isLast={i === meters.length - 1}
              meterInfo={m}
            />
          );
        })}

        {/* Demand charge row */}
        {bill.demandKW && (
          <div className="border-t border-neutral-200 px-6 py-3 flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-700">{t("bill.demandCharge")}</p>
              <p className="text-xs text-neutral-500">
                {t("bill.demandDetail", { kw: bill.demandKW })}
              </p>
            </div>
            <p className="text-sm font-semibold text-semantic-warning-text font-mono tabular-nums">
              {formatCurrency(calculateDemandCharge(bill.demandKW))}
            </p>
          </div>
        )}

        <div className="border-t-2 border-brand-primary/20 px-6 py-4 flex items-center justify-between bg-white">
          <span className="text-base font-bold text-neutral-900">{t("bill.totalDue")}</span>
          <span className="text-2xl font-display font-bold text-brand-primary-text tabular-nums">
            {formatCurrency(bill.total)}
          </span>
        </div>
      </div>

      {/* F-2 applies to every branch, not just the residential one: a settled
          P3 sees the same contradiction. */}
      <div className="flex flex-col sm:flex-row gap-3">
        {!billState.isSettled && (
          <Link href="/batch-pay" className="btn-primary flex-1 justify-center">
            {t("bill.payNow", { amount: formatCurrency(bill.total) })}
          </Link>
        )}
        <BillPdfButton personaId={persona.id} />
      </div>

      <PaymentHistoryLink />
    </div>
  );
}

function MeterRow({
  meterId,
  name,
  kwh,
  pct,
  total,
  anomaly,
  isLast,
  meterInfo,
}: {
  meterId: string;
  name: string;
  kwh: number;
  pct: number;
  total: number;
  anomaly?: string;
  isLast: boolean;
  meterInfo?: Meter;
}) {
  const t = useT();
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`border-t border-neutral-200 ${isLast ? "" : ""}`}>
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-6 py-3 flex items-center justify-between hover:bg-neutral-50 transition-colors duration-fast"
        aria-expanded={expanded}
        aria-label={t("bill.meterRowAria", { name, kwh: formatKwh(kwh) })}
      >
        <div className="flex items-center gap-3 text-left">
          <span className="text-xs font-mono tabular-nums text-neutral-500 w-24 flex-shrink-0">
            {meterId}
          </span>
          <div>
            <p className="text-sm font-medium text-neutral-900">{name}</p>
            {anomaly && (
              <p className="text-xs text-semantic-warning-text">{anomaly}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-neutral-500">{pct}%</span>
          <span className="text-sm font-semibold text-neutral-900 font-mono tabular-nums">
            {formatCurrency(total)}
          </span>
          {expanded ? (
            <ChevronUp size={14} className="text-neutral-500" aria-hidden />
          ) : (
            <ChevronDown size={14} className="text-neutral-500" aria-hidden />
          )}
        </div>
      </button>
      {expanded && (
        <div className="px-6 pb-3 bg-neutral-50 text-xs text-neutral-500 space-y-1">
          <p>{t("bill.meterUsage", { kwh: formatKwh(kwh) })}</p>
          {meterInfo && (
            <p>{t("bill.meterIdLocation", { id: meterInfo.id, location: meterInfo.description })}</p>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Portfolio bill view (P4)
// ---------------------------------------------------------------------------
function PortfolioBillView({
  persona,
}: {
  persona: ReturnType<typeof useActivePersona>;
}) {
  const accounts = persona.managedAccounts ?? [];
  const t = useT();
  const tag = useLocaleTag();
  const billState = useBillState(persona.id);

  return (
    <div className="space-y-4">
      <div className="card-surface space-y-0 overflow-hidden">
        {accounts.map((a, i) => (
          <div
            key={a.accountNumber}
            className={`${i > 0 ? "border-t border-neutral-200" : ""} px-6 py-4 flex items-center justify-between`}
          >
            <div>
              <p className="text-sm font-medium text-neutral-900">
                {a.propertyName}
              </p>
              <p className="text-xs text-neutral-500 font-mono tabular-nums">
                #{a.accountNumber}
              </p>
              {a.anomalyBadge && (
                <p className="text-xs text-semantic-warning-text mt-0.5">
                  ⚠ {a.anomalyBadge}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="text-sm font-bold text-neutral-900 font-mono tabular-nums">
                {formatCurrency(a.currentBillAmount)}
              </p>
              <p className="text-xs text-neutral-500">
                {billState.isSettled
                  ? t("billing.paidOn", { date: formatDate(billState.settledOnIso, tag) })
                  : t("bill.dueBadge", { date: formatDate(a.currentBillDueDate, tag) })}
              </p>
            </div>
          </div>
        ))}
        <div className="border-t-2 border-brand-primary/20 px-6 py-4 flex items-center justify-between bg-white">
          <span className="text-base font-bold text-neutral-900">
            {t("bill.portfolioTotal")}
          </span>
          <span className="text-2xl font-display font-bold text-brand-primary-text tabular-nums">
            {formatCurrency(calculatePortfolioTotal(accounts))}
          </span>
        </div>
      </div>
      {!billState.isSettled && (
        <Link href="/batch-pay" className="btn-primary w-full justify-center">
          {t("bill.batchPayAllAccounts")}
        </Link>
      )}

      <PaymentHistoryLink />
    </div>
  );
}

function BillLine({
  label,
  detail,
  amount,
}: {
  label: React.ReactNode;
  detail: string;
  amount: number;
}) {
  return (
    <div className="px-6 py-3 flex items-center justify-between">
      <div>
        <p className="text-sm text-neutral-900">{label}</p>
        <p className="text-xs text-neutral-500">{detail}</p>
      </div>
      <p className="text-sm font-medium text-neutral-900 font-mono tabular-nums">
        {formatCurrency(amount)}
      </p>
    </div>
  );
}
